import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export type ContestTimezoneId = "browser" | "spain" | "saudi";

export interface ContestTimezoneOption {
  id: ContestTimezoneId;
  label: string;
  iana: string | null;
}

export const DEFAULT_TIMEZONE_ID: ContestTimezoneId = "browser";

export const CONTEST_TIMEZONES: ContestTimezoneOption[] = [
  { id: "browser", label: "Browser local", iana: null },
  { id: "spain", label: "Spain (Madrid)", iana: "Europe/Madrid" },
  { id: "saudi", label: "Saudi Arabia (Riyadh)", iana: "Asia/Riyadh" },
];

const resolveIana = (timezoneId: ContestTimezoneId): string => {
  if (timezoneId === "spain") return "Europe/Madrid";
  if (timezoneId === "saudi") return "Asia/Riyadh";
  return dayjs.tz.guess();
};

// Treat picker wall-clock as time in the selected zone → UTC ISO `...Z`.
export const wallTimeToUtcIso = (
  wallTime: Dayjs,
  timezoneId: ContestTimezoneId
): string => {
  const wall = wallTime.format("YYYY-MM-DD HH:mm:ss");
  return dayjs.tz(wall, resolveIana(timezoneId)).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

// UTC ISO → wall-clock Dayjs for the DateTimePicker in the selected zone.
export const utcIsoToWallTime = (
  utcIso: string,
  timezoneId: ContestTimezoneId
): Dayjs | null => {
  const normalized = utcIso.trim().replace("Z", "+00:00");
  const utc = dayjs(normalized);
  if (!utc.isValid()) return null;

  const inZone = utc.tz(resolveIana(timezoneId));
  // DateTimePicker expects a plain local Dayjs with the same wall clock numbers.
  return dayjs()
    .year(inZone.year())
    .month(inZone.month())
    .date(inZone.date())
    .hour(inZone.hour())
    .minute(inZone.minute())
    .second(inZone.second())
    .millisecond(0);
};

// Keep the same UTC instant when switching timezone input mode.
export const convertWallTimeBetweenZones = (
  wallTime: Dayjs,
  fromTimezoneId: ContestTimezoneId,
  toTimezoneId: ContestTimezoneId
): Dayjs => {
  if (fromTimezoneId === toTimezoneId) return wallTime;
  const utcIso = wallTimeToUtcIso(wallTime, fromTimezoneId);
  return utcIsoToWallTime(utcIso, toTimezoneId) ?? wallTime;
};
